import React, { useState, useEffect } from "react";
import styles from "styles/card.module.scss";
import Timer from "../../Timer";
import { handleKeyDown } from "Utils/handleKeyDown";
import { ClientImages } from "contexts/CommonContext";

interface CardProps {
  responseData: any;
  clientImages: ClientImages | null;
  clientName: string;
  handleResendIpinClick: () => void;
  handleCancelClick: () => void;
  handleSubmit: (e: any) => void;
  handleOobSubmit: () => void;
  notification: string;
  notificationType: "success" | "error";
  updateNotification: (message: string) => void;
  isResendIpinClicked: boolean;
  isResendLimitAvailable: boolean;
}

const Card: React.FC<CardProps> = ({
  notification,
  notificationType,
  responseData,
  clientImages,
  clientName,
  handleCancelClick,
  handleSubmit,
  updateNotification,
}) => {

  const {
    maskedCardNumber,
    ipAddress,
    transactionTime,
    timeout,
    amount,
    merchantName,
    currencyCode,
    challengeType,
    challengeLength,
  } = responseData || {};

  const [timeRemaining, setTimeRemaining] = useState(timeout);

  useEffect(() => {
    setTimeRemaining(timeout);
  }, [timeout]);

  useEffect(() => {
    if (timeRemaining > 0) {
      const timerId = setInterval(() => {
        setTimeRemaining((prev: number) => prev - 1);
      }, 1000);
      return () => clearInterval(timerId);
    }
  }, [timeRemaining]);

  if (clientImages) {
    document.title = clientName;
    const link = document.querySelector("link[rel~='icon']");
    if (link) link.setAttribute("href", clientImages.faviconUrl);
  }

    const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.topBar}>
          {clientImages && (
            <>
              <img
                src={clientImages.logoUrl}
                alt={`${clientName} Logo`}
                className="issuer-logo"
              />
              <img
                src={clientImages.networkLogoUrl}
                alt="Card Network Logo"
                className="card-logo"
              />
            </>
          )}
        </div>

        {/* IPIN challenge */}
        {(!challengeType || challengeType === 2 || challengeType === 8) && (
          <div className={styles.contentContainer}>
            <div className={styles.contentBox}>
              {merchantName && (
                <div>
                  <span>Merchant</span>
                  <span>{merchantName}</span>
                </div>
              )}
              {amount && (
                <div>
                  <span>Amount</span>
                  <span>
                    {currencyCode} {amount}
                  </span>
                </div>
              )}
              <div>
                <span>Card number</span>
                <span>{maskedCardNumber}</span>
              </div>
            </div>
            <div className={styles.msg}>
              Please use the IPIN to authenticate the transaction.
            </div>
            {notification && (
              <div
                className={`${styles.otpWarningSection} ${
                  notificationType === "error" ? styles.otpErrorSection : ""
                }`}
              >
                <div>
                  <span>{notification}</span>
                  <button
                    type="button"
                    className={styles.dismissBtn}
                    onClick={() => updateNotification("")}
                    aria-label="Dismiss"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}
            <div className={`${styles.msg} ${styles.darkNote}`}>
              Note: The I-PIN page will expire in:{" "}
              <span>{formatTime(timeRemaining)}</span> seconds.
            </div>
            <form onSubmit={handleSubmit}>
              <div className={styles.inputContainer}>
                <label htmlFor="ipin" className="ipin-label">
                  Enter IPIN
                </label>
                <input
                  type="password"
                  pattern="\d*"
                  maxLength={challengeLength}
                  inputMode="numeric"
                  autoFocus
                  id="ipin"
                  name="ipin"
                  placeholder="Enter IPIN"
                  onKeyDown={(e) => handleKeyDown(e, updateNotification)}
                />
                <div className={styles.resendCancelRow}>
                  <div className={styles.cancelButtonSection}>
                    <button type="button" onClick={handleCancelClick}>
                      Cancel
                    </button>
                  </div>
                </div>
                <button type="submit" className={styles.submitBtn}>
                  Submit
                </button>
              </div>
            </form>
          </div>
        )}

        <div className={styles.textBorder}></div>
        <div className={styles.txnMsg}>
          Your transaction is processed through a secure 128-bit https internet
          connection based on secure socket layer technology. For security
          purpose, your IP address {ipAddress} and access time {transactionTime}{" "}
          have been logged.
        </div>
      </div>
      <div className={styles.note}>
        <span>Note:</span> Please do not press the back button or exit the
        app/website while we complete the transaction. This page will
        automatically timeout after {timeout} seconds.
      </div>
    </div>
  );
};

export default Card;
